/**
 * DataProvider Abstraction Layer
 *
 * Server PC uses DexieProvider → direct IndexedDB access
 * Client PC uses RemoteProvider → HTTP fetch to server
 *
 * All data operations go through this interface so the UI layer
 * doesn't need to know whether it's local or remote.
 */

import { db } from '../db/db';
import type { Ledger, Voucher, VoucherEntry, InventoryEntry, StockItem, Unit } from '../db/db';

export interface DataProvider {
  mode: 'local' | 'remote';
  serverURL?: string;

  companies: {
    list(): Promise<any[]>;
    get(id: number): Promise<any | undefined>;
    update(id: number, data: any): Promise<void>;
  };
  groups: {
    list(companyId: number): Promise<any[]>;
    get(id: number): Promise<any | undefined>;
    add(data: any): Promise<number>;
    update(id: number, data: any): Promise<void>;
  };
  ledgers: {
    list(companyId: number): Promise<Ledger[]>;
    get(id: number): Promise<Ledger | undefined>;
    add(data: any): Promise<number>;
    update(id: number, data: any): Promise<void>;
    getByGroupId(groupId: number): Promise<Ledger[]>;
  };
  vouchers: {
    list(companyId: number, opts?: { fromDate?: string; toDate?: string; includeDeleted?: boolean }): Promise<Voucher[]>;
    get(id: number): Promise<Voucher | undefined>;
    add(data: any): Promise<number>;
    update(id: number, data: any): Promise<void>;
  };
  voucherEntries: {
    list(voucherId?: number): Promise<VoucherEntry[]>;
    getByLedgerId(ledgerId: number): Promise<VoucherEntry[]>;
    getByVoucherIds(ids: number[]): Promise<VoucherEntry[]>;
    add(data: any): Promise<number>;
  };
  inventoryEntries: {
    list(voucherId?: number): Promise<InventoryEntry[]>;
    add(data: any): Promise<number>;
  };
  stockItems: {
    list(companyId: number): Promise<StockItem[]>;
    get(id: number): Promise<StockItem | undefined>;
    add(data: any): Promise<number>;
  };
  stockGroups: {
    list(companyId: number): Promise<any[]>;
  };
  units: {
    list(companyId: number): Promise<Unit[]>;
    get(id: number): Promise<Unit | undefined>;
  };
  voucherTypes: {
    list(companyId: number): Promise<any[]>;
  };
  employees: {
    list(companyId: number): Promise<any[]>;
  };
}

// ── DexieProvider — direct local IndexedDB (server PC) ───────────────────────

export const DexieProvider: DataProvider = {
  mode: 'local',

  companies: {
    list: () => db.companies.toArray(),
    get: (id) => db.companies.get(id),
    update: (id, data) => db.companies.update(id, data).then(() => {}),
  },

  groups: {
    list: (companyId) => db.groups.where('companyId').equals(companyId).toArray(),
    get: (id) => db.groups.get(id),
    add: (data) => db.groups.add(data) as Promise<number>,
    update: (id, data) => db.groups.update(id, data).then(() => {}),
  },

  ledgers: {
    list: (companyId) => db.ledgers.where('companyId').equals(companyId).toArray(),
    get: (id) => db.ledgers.get(id),
    add: (data) => db.ledgers.add(data) as Promise<number>,
    update: (id, data) => db.ledgers.update(id, data).then(() => {}),
    getByGroupId: (groupId) => db.ledgers.where('groupId').equals(groupId).toArray(),
  },

  vouchers: {
    list: (companyId, opts) => {
      let query = db.vouchers.where('companyId').equals(companyId);
      return query.filter(v => {
        if (!opts?.includeDeleted && v.deleted) return false;
        if (opts?.fromDate && v.date < opts.fromDate) return false;
        if (opts?.toDate && v.date > opts.toDate) return false;
        return true;
      }).toArray();
    },
    get: (id) => db.vouchers.get(id),
    add: (data) => db.vouchers.add(data) as Promise<number>,
    update: (id, data) => db.vouchers.update(id, data).then(() => {}),
  },

  voucherEntries: {
    list: (voucherId) => voucherId
      ? db.voucherEntries.where('voucherId').equals(voucherId).toArray()
      : db.voucherEntries.toArray(),
    getByLedgerId: (ledgerId) => db.voucherEntries.where('ledgerId').equals(ledgerId).toArray(),
    getByVoucherIds: (ids) => ids.length > 0
      ? db.voucherEntries.where('voucherId').anyOf(ids).toArray()
      : Promise.resolve([]),
    add: (data) => db.voucherEntries.add(data) as Promise<number>,
  },

  inventoryEntries: {
    list: (voucherId) => voucherId
      ? db.inventoryEntries.where('voucherId').equals(voucherId).toArray()
      : db.inventoryEntries.toArray(),
    add: (data) => db.inventoryEntries.add(data) as Promise<number>,
  },

  stockItems: {
    list: (companyId) => db.stockItems.where('companyId').equals(companyId).toArray(),
    get: (id) => db.stockItems.get(id),
    add: (data) => db.stockItems.add(data) as Promise<number>,
  },

  stockGroups: {
    list: (companyId) => db.stockGroups.where('companyId').equals(companyId).toArray(),
  },

  units: {
    list: (companyId) => db.units.where('companyId').equals(companyId).toArray(),
    get: (id) => db.units.get(id),
  },

  voucherTypes: {
    list: (companyId) => db.voucherTypes.where('companyId').equals(companyId).toArray(),
  },

  employees: {
    list: (companyId) => db.employees.where('companyId').equals(companyId).toArray(),
  },
};

// ── RemoteProvider — HTTP fetch to server (client PCs via browser) ────────────

export function createRemoteProvider(serverURL: string): DataProvider {
  const api = async (method: string, path: string, body?: any) => {
    const res = await fetch(`${serverURL}/api/${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  };

  return {
    mode: 'remote',
    serverURL,

    companies: {
      list: () => api('GET', 'companies'),
      get: (id) => api('GET', `companies/${id}`),
      update: (id, data) => api('PUT', `companies/${id}`, data),
    },

    groups: {
      list: (companyId) => api('GET', `groups?companyId=${companyId}`),
      get: (id) => api('GET', `groups/${id}`),
      add: (data) => api('POST', 'groups', data).then(r => r.id),
      update: (id, data) => api('PUT', `groups/${id}`, data),
    },

    ledgers: {
      list: (companyId) => api('GET', `ledgers?companyId=${companyId}`),
      get: (id) => api('GET', `ledgers/${id}`),
      add: (data) => api('POST', 'ledgers', data).then(r => r.id),
      update: (id, data) => api('PUT', `ledgers/${id}`, data),
      getByGroupId: (groupId) => api('GET', `ledgers?groupId=${groupId}`),
    },

    vouchers: {
      list: (companyId, opts) => {
        let url = `vouchers?companyId=${companyId}`;
        if (opts?.fromDate) url += `&fromDate=${opts.fromDate}`;
        if (opts?.toDate) url += `&toDate=${opts.toDate}`;
        return api('GET', url);
      },
      get: (id) => api('GET', `vouchers/${id}`),
      add: (data) => api('POST', 'vouchers', data).then(r => r.id),
      update: (id, data) => api('PUT', `vouchers/${id}`, data),
    },

    voucherEntries: {
      list: (voucherId) => voucherId
        ? api('GET', `voucherEntries?voucherId=${voucherId}`)
        : api('GET', 'voucherEntries'),
      getByLedgerId: (ledgerId) => api('GET', `voucherEntries?ledgerId=${ledgerId}`),
      getByVoucherIds: (ids) => api('POST', 'voucherEntries/byVoucherIds', { ids }),
      add: (data) => api('POST', 'voucherEntries', data).then(r => r.id),
    },

    inventoryEntries: {
      list: (voucherId) => voucherId
        ? api('GET', `inventoryEntries?voucherId=${voucherId}`)
        : api('GET', 'inventoryEntries'),
      add: (data) => api('POST', 'inventoryEntries', data).then(r => r.id),
    },

    stockItems: {
      list: (companyId) => api('GET', `stockItems?companyId=${companyId}`),
      get: (id) => api('GET', `stockItems/${id}`),
      add: (data) => api('POST', 'stockItems', data).then(r => r.id),
    },

    stockGroups: {
      list: (companyId) => api('GET', `stockGroups?companyId=${companyId}`),
    },

    units: {
      list: (companyId) => api('GET', `units?companyId=${companyId}`),
      get: (id) => api('GET', `units/${id}`),
    },

    voucherTypes: {
      list: (companyId) => api('GET', `voucherTypes?companyId=${companyId}`),
    },

    employees: {
      list: (companyId) => api('GET', `employees?companyId=${companyId}`),
    },
  };
}

// ── Active Provider — singleton, switches between local and remote ────────────

let activeProvider: DataProvider = DexieProvider;

export function getDataProvider(): DataProvider {
  return activeProvider;
}

export function setDataProvider(provider: DataProvider): void {
  activeProvider = provider;
}

export function isRemoteMode(): boolean {
  return activeProvider.mode === 'remote';
}

export function connectToServer(serverURL: string): void {
  activeProvider = createRemoteProvider(serverURL);
}

export function disconnectFromServer(): void {
  activeProvider = DexieProvider;
}
